import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiError } from '../../lib/utils';
import CustomerLayout from '../../components/layouts/CustomerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { User, Building2, Mail, Phone, MapPin, Save, Pencil } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    company_address: '',
    company_city: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.company_phone || user.phone || '',
        company_name: user.company_name || '',
        company_address: user.company_address || '',
        company_city: user.company_city || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!formData.full_name || !formData.email || !formData.company_name) {
      toast.error('Name, email, and company name are required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.company_phone || user.phone || '',
        company_name: user.company_name || '',
        company_address: user.company_address || '',
        company_city: user.company_city || '',
      });
    }
  };

  return (
    <CustomerLayout>
      <div className="animate-fade-in" data-testid="settings-page">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="page-title">Settings</h1>
            {!editing && (
              <Button onClick={() => setEditing(true)} variant="outline" className="gap-2" data-testid="edit-profile-btn">
                <Pencil className="h-4 w-4" /> Edit Profile
              </Button>
            )}
          </div>

          <Card className="card-elevated mb-6">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                  <User className="h-5 w-5 text-teal-600" />
                </div>
                <CardTitle className="card-title">Personal Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full Name</Label>
                  {editing ? (
                    <Input name="full_name" value={formData.full_name} onChange={handleChange} className="mt-1" data-testid="edit-full-name" />
                  ) : (
                    <p className="mt-1 text-slate-900 font-medium" data-testid="display-full-name">{formData.full_name || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
                  {editing ? (
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} className="mt-1" data-testid="edit-email" />
                  ) : (
                    <div className="flex items-center mt-1">
                      <Mail className="h-4 w-4 text-slate-400 mr-2" />
                      <p className="text-slate-900" data-testid="display-email">{formData.email || '-'}</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone Number</Label>
                  {editing ? (
                    <Input name="phone" value={formData.phone} onChange={handleChange} className="mt-1" data-testid="edit-phone" />
                  ) : (
                    <div className="flex items-center mt-1">
                      <Phone className="h-4 w-4 text-slate-400 mr-2" />
                      <p className="text-slate-900" data-testid="display-phone">{formData.phone || '-'}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated mb-6">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                </div>
                <CardTitle className="card-title">Company Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company Name</Label>
                {editing ? (
                  <Input name="company_name" value={formData.company_name} onChange={handleChange} className="mt-1" data-testid="edit-company-name" />
                ) : (
                  <div className="flex items-center mt-1">
                    <Building2 className="h-4 w-4 text-slate-400 mr-2" />
                    <p className="text-slate-900 font-medium" data-testid="display-company-name">{formData.company_name || '-'}</p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company Address</Label>
                {editing ? (
                  <Input name="company_address" value={formData.company_address} onChange={handleChange} className="mt-1" data-testid="edit-company-address" />
                ) : (
                  <div className="flex items-center mt-1">
                    <MapPin className="h-4 w-4 text-slate-400 mr-2" />
                    <p className="text-slate-900" data-testid="display-company-address">{formData.company_address || '-'}</p>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">City</Label>
                {editing ? (
                  <Input name="company_city" value={formData.company_city} onChange={handleChange} className="mt-1" data-testid="edit-company-city" />
                ) : (
                  <div className="flex items-center mt-1">
                    <MapPin className="h-4 w-4 text-slate-400 mr-2" />
                    <p className="text-slate-900" data-testid="display-company-city">{formData.company_city || '-'}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {editing && (
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleCancel} data-testid="cancel-edit-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary gap-2" data-testid="save-profile-btn">
                <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default Settings;
